import { ClassifierData } from 'types/classifiers';
import { CustomFieldDto } from 'types/customFields';
import { Team, TeamCreateDto, TeamForm } from 'types/teams';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';

type Option = { label: string; value: string };

/** The edit form's values for a stored team, its custom fields' values as `useCustomFields` converts them. */
export const teamDtoToForm = (
    team: Team,
    classifiers: Record<string, ClassifierData>,
    customFieldValues: Record<string, unknown>
): TeamForm => {
    const flatBusinessAreas = (team.teamBusinessAreas || []).map((val) => val.businessAreaTypeCode);
    return {
        name: team.name ?? '',
        abbreviation: team.abbreviation ?? '',
        teamType:
            (getOptionsFromClassifier(classifiers['TEAM_TYPE']) as Option[]).find(
                (val) => val.value === team.teamTypeCode
            ) ?? null,
        isActive: team.isActive ?? true,
        parentTeam: team.parentTeam ?? null,
        teamPracticeAreas: team.teamPracticeAreas ?? [],
        emailAddress: team.emailAddress ?? '',
        readonlyTeamEmployees: team.teamEmployees ?? [],
        teamOffices: team.teamOffices ?? [],
        isParent: team.isParent ?? false,
        teamBusinessAreas: getOptionsFromClassifier(classifiers['BUSINESS_AREA_TYPE']).filter(
            (val) => typeof val.value === 'string' && flatBusinessAreas.includes(val.value)
        ) as Option[],
        dmsTeam: team.dmsTeamId
            ? {
                  id: team.dmsTeamId,
                  displayName: team.dmsTeamName || t('teams.unknown-dms-team', { id: team.dmsTeamId }),
              }
            : null,
        ...customFieldValues,
    };
};

interface RequestContext {
    customFields: CustomFieldDto[];
    /** False keeps the team's DMS group as it was: only a back-office user edits it. */
    canEditDmsTeam?: boolean;
    /** The form as the team was loaded, for the DMS group a user without that right cannot change. */
    initialForm?: TeamForm;
}

export const teamFormToRequest = (
    form: TeamForm,
    { customFields, canEditDmsTeam, initialForm }: RequestContext
): TeamCreateDto => {
    const dmsTeamId =
        canEditDmsTeam === false && initialForm?.dmsTeam?.id ? initialForm.dmsTeam.id : (form?.dmsTeam?.id ?? null);

    return {
        name: form.name,
        abbreviation: form.abbreviation,
        teamTypeCode: form?.teamType ? form.teamType.value : null,
        emailAddress: form?.emailAddress ?? null,
        isActive: form.isActive,
        isParent: form.isParent,
        parentTeam: !form.isParent ? form?.parentTeam : null,
        teamPracticeAreas: form?.teamType?.value === 'PRACTICE_GROUP' ? (form.teamPracticeAreas ?? []) : [],
        teamOffices: form.teamOffices ?? [],
        teamBusinessAreas:
            form?.teamType?.value === 'SECTOR_GROUP' ? form.teamBusinessAreas.map((area) => area.value) : [],
        dmsTeamId,
        ...(customFields.length > 0 ? { customFields: customFields } : {}),
    };
};

/** The form fields each card of the team page edits; the type and the fields it drives stay together. */
export const TEAM_SECTION_FIELDS = {
    team: [
        'teamType',
        'dmsTeam',
        'name',
        'abbreviation',
        'isParent',
        'parentTeam',
        'teamPracticeAreas',
        'teamBusinessAreas',
        'isActive',
    ],
    contact: ['teamOffices', 'emailAddress'],
    'custom-fields': [],
} satisfies Record<string, (keyof TeamForm)[]>;

export type TeamSectionId = keyof typeof TEAM_SECTION_FIELDS;
